import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import './Performance.scss';
import { useQuery } from '@tanstack/react-query';
import 'react-quill/dist/quill.bubble.css'; // Import the bubble theme for ReactQuill
import 'react-quill/dist/quill.snow.css';
import {
  Button,
  Accordion,
  AccordionHeader,
  AccordionBody,
  AccordionItem,
  Input,
  Badge,
  Spinner,
} from 'reactstrap';
import Icon from '@mdi/react';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';
import { mdiAccountOutline, mdiTrashCanOutline, mdiChevronLeft } from '@mdi/js';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import EmployeeDetails from './Components/EmployeeDetails/EmployeeDetails';
import downloadIcon from '../../assets/images/performance/download.svg';
import addIcon from '../../assets/images/performance/addIcon.png';
import pcmHeader from '../../assets/images/performance/pcmHeader.svg';
import performanceHeaderIcon from '../../assets/images/performance/performanceHeaderIcon.svg';
import messageIcon from '../../assets/images/performance/messageIcon.png';
import rightArrowIcon from '../../assets/images/performance/rightArrowIcon.svg';
import ratingRightArrow from '../../assets/images/performance/ratingRightArrow.svg';
import AddKPI from './Components/AddKPI/AddKPI';
import { ManagerAutocompleteSelect } from '../../components/Common';
import {
  fetchCalibrationWeightageNDueDate,
  fetchKPIs,
  fetchPerformance,
} from '../../store/apis/performanceService';
import HeaderSteps from './Components/HeaderSteps/HeaderSteps';
import KPIComment from './Components/KPIComment/KPIComment';
import DeleteModal from './Components/DeleteModal/DeleteModal';
import AddWeightage from './Components/Weightage/AddWeightage';
import ManagerComment from './Components/ManagerComment/ManagerComment';
import SelfRating from './Components/Rating/SelfRating';
import ManagerRating from './Components/Rating/ManagerRating';
import {
  ACTIVE_STEP,
  PREVIOUS_STEP,
} from './Components/ConfirmationModal/constants';
import ConfirmationModal from './Components/ConfirmationModal/ConfirmationModal';
import AcceptSubmitModal from './Components/AcceptSubmitModal/AcceptSubmitModal';
import { fetchEmployeeDetails } from '../../store/apis/employeeService';
import { getAuthUser } from '../../utils/auth.util';
import Summary from './Components/Summary/Summary';
import { ExportUtil } from '../../utils';
import {
  calculateWeightage,
  checkUserAccess,
  getPerformanceWeightage,
  showKPIs,
  validateSubmission,
} from './common';
import PCMKPIHeader from './Components/Details/PCMKPIHeader';
import PeroformanceKPIHeader from './Components/Details/PeroformanceKPIHeader';
import { getCurrentFinancialPeriod } from '../../utils/date.util';
import SelfRatingComment from './Components/SelfRatingComment/SelfRatingComment';
import { getCompanyCode } from '../../config/axios.config';
import { fetchHRCodeList } from '../../store/apis/commonService';

function Performance() {
  document.title = 'Performance | Envision Daikin';
  const location = useLocation();
  const navigate = useNavigate();
  const { id: employeeId } = useParams();
  const companyCode = getCompanyCode();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedKPI, setSelectedKPI] = useState();
  const [selectedPeriod, setSelectedPeriod] = useState(
    getCurrentFinancialPeriod(),
  );
  const [pcmWeigtage, setPCMWeigtage] = useState(0);
  const [performanceWeigtage, setPerformanceWeigtage] = useState(0);
  const [callAPI, setCallAPI] = useState(false);
  const authUser = getAuthUser();

  const { data: currentFiscalYear } = useQuery({
    queryKey: ['currentFiscalYear'],
    queryFn: () => fetchHRCodeList({ type: 'FISCAL_YEAR' }),
    enabled: true,
    keepPreviousData: true,
  });

  const { data: KPIs, isLoading: isKPILoading } = useQuery({
    queryKey: ['privileges'],
    queryFn: fetchKPIs,
  });

  const { data: user } = useQuery({
    queryKey: ['employeeDetails'],
    queryFn: async () => {
      const res = await fetchEmployeeDetails({
        type: 'PROFILE',
      });
      return res?.[0];
    },
    refetchOnWindowFocus: true,
    staleTime: 0, // Data is always stale, so refetches on mount
    cacheTime: 0,
  });

  useEffect(() => {
    if (user && !employeeId) {
      setSelectedEmployee({
        value: user?.EmployeeNo?.trim(),
        label: user?.EmployeeName?.trim(),
      });
      setCallAPI(true);
    }
    if (employeeId) {
      setSelectedEmployee({
        value: employeeId?.trim(),
        label: employeeId?.trim(),
      });
      setCallAPI(true);
    }
  }, [user, employeeId]);

  const {
    data: performanceHeader,
    isLoading: performanceHeaderLoading,
    refetch: refetchPerformanceHeader,
  } = useQuery({
    queryKey: ['performanceHeader', selectedEmployee],
    queryFn: () =>
      fetchPerformance({
        type: 'HEADER',
        employeeId: selectedEmployee?.value?.trim(),
        period: selectedPeriod || getCurrentFinancialPeriod(),
      }),
    enabled: !!selectedEmployee && callAPI,
  });

  const { data: performanceDetails, refetch: refetchPerformanceDetails } =
    useQuery({
      queryKey: ['performanceDetails', selectedEmployee],
      queryFn: () =>
        fetchPerformance({
          type: 'DETAIL',
          employeeId: selectedEmployee?.value?.trim(),
          period: selectedPeriod || getCurrentFinancialPeriod(),
        }),
      enabled: !!selectedEmployee && callAPI,
    });

  const { data: performanceDueDate, refetch: refetchPerformanceDueDate } =
    useQuery({
      queryKey: ['performanceDueDate', selectedEmployee],
      queryFn: () =>
        fetchCalibrationWeightageNDueDate({
          type: 'duedate',
          coCd: companyCode,
          fiscalYear: selectedPeriod || getCurrentFinancialPeriod(),
        }),
      enabled: !!selectedEmployee && callAPI,
    });

  const {
    data: weightagePercentage,
    isLoading: weightagePercentageLoading,
    refetch: refetchweightagePercentage,
  } = useQuery({
    queryKey: ['weightagePercentage', selectedEmployee],
    queryFn: () =>
      fetchCalibrationWeightageNDueDate({
        type: 'weightage',
        fiscalYear: selectedPeriod || getCurrentFinancialPeriod(),
      }),
    enabled: !!selectedEmployee && callAPI,
  });

  useEffect(() => {
    if (selectedEmployee) {
      refetchPerformanceHeader();
      refetchPerformanceDetails();
    }
  }, [
    selectedEmployee,
    selectedPeriod,
    refetchPerformanceHeader,
    refetchPerformanceDetails,
  ]);

  useEffect(() => {
    if (selectedPeriod) {
      refetchweightagePercentage();
      refetchPerformanceDueDate();
    }
  }, [selectedPeriod, refetchweightagePercentage, refetchPerformanceDueDate]);

  useEffect(() => {
    if (performanceDetails) {
      const { pcmWtg, performanceWtg } = calculateWeightage(performanceDetails);
      setPCMWeigtage(pcmWtg);
      setPerformanceWeigtage(performanceWtg);
    }
  }, [performanceDetails]);

  const { isEmployee, isCompleted, isCalibrationCompleted } = checkUserAccess(
    authUser,
    selectedEmployee,
    performanceHeader,
  );

  const [offcanvasKPI, setOffcanvasKPI] = useState(false);
  const toggleOffcanvasKPI = (detail, e) => {
    e?.stopPropagation();
    setSelectedKPI(detail);
    setOffcanvasKPI(!offcanvasKPI);
  };

  const [offcanvasComment, setOffcanvasComment] = useState(false);
  const toggleOffcanvasComment = (e, detail) => {
    e?.stopPropagation();
    setSelectedKPI(detail);
    setOffcanvasComment(!offcanvasComment);
  };

  const [modalDelete, setModalDelete] = useState(false);
  const toggleDelete = (detail, e) => {
    e?.stopPropagation();
    setSelectedKPI(detail);
    setModalDelete(!modalDelete);
  };

  const [open, setOpen] = useState();

  const toggle = (event, id) => {
    // Prevent toggle if click originated from modal
    if (event?.target?.closest('.modal')) {
      event?.stopPropagation();
      return;
    }

    if (open === id) {
      setOpen();
    } else {
      setOpen(id);
    }
  };

  const [hasPreviousCall, setPreviousCall] = useState(false);
  const [modalConfirmTitle, setModalConfirmTitle] = useState();
  const [modalConfirm, setModalConfirm] = useState(false);

  const toggleMovePrev = () => {
    setPreviousCall(true);
    setModalConfirmTitle(PREVIOUS_STEP);
    setModalConfirm(!modalConfirm);
  };

  const [modalSubmit, setModalSubmit] = useState(false);

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    if (!performanceDetails || performanceDetails?.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);

    const calibrationResult = performanceHeader?.[0]?.Calibration_Result;

    let exportData = null;

    if (calibrationResult === 1) {
      exportData = performanceDetails?.map(item => ({
        KPI_Name: item?.KPI_Name,
        KPI_Type: item?.KPI_Type,
        KPI_Details: item?.KPI_Details,
        Weightage: item?.Weightage,
        Rating_Self: item?.Rating_Self,
        Rating_Mgr: '-',
      }));
    } else {
      exportData = performanceDetails?.map(item => ({
        KPI_Name: item?.KPI_Name,
        KPI_Type: item?.KPI_Type,
        KPI_Details: item?.KPI_Details,
        Weightage: item?.Weightage,
        Rating_Self: item?.Rating_Self,
        Rating_Mgr: item?.Rating_Mgr,
      }));
    }

    ExportUtil.downloadExcel(exportData, {
      fileName: `Performance_${timesTamp}.xlsx`,
      columns: {
        KPI_Name: 'KPI Name',
        KPI_Type: 'KPI Type',
        KPI_Details: 'KPI Details',
        Weightage: 'Weightage',
        Rating_Self: 'Self Rating',
        Rating_Mgr: 'Manager Rating',
      },
    });
  };

  const toggleSubmit = () => {
    setPreviousCall(false);
    if (performanceHeader?.[0]?.Calibration_Result === 2) {
      setModalConfirm(false);
      setModalSubmit(true);
      return;
    }

    const { isValid, message } = validateSubmission(
      performanceHeader?.[0],
      performanceDetails,
      weightagePercentage,
    );
    if (!isValid) {
      toast.error(message, {
        className: 'toast-error',
      });
      return;
    }

    setModalConfirmTitle(ACTIVE_STEP);
    setModalSubmit(false);
    setModalConfirm(true);
  };

  const showComment = detail => {
    if (!detail?.KPI_Comments && isEmployee) {
      return (
        <Button
          className="btn-primary-light btn btn-primary actionItem fs-6"
          onClick={e => toggleOffcanvasComment(e, detail)}
        >
          <img src={messageIcon} alt="Add Comment" height={14} />
          Comment
        </Button>
      );
    }
    return (
      <Button
        className="btn-primary-light btn btn-primary actionItem fs-6"
        onClick={e => toggleOffcanvasComment(e, detail)}
      >
        <img src={messageIcon} alt="View Comments" height={14} />
        {`${isEmployee ? 'Comment' : 'View Comments'}`}
      </Button>
    );
  };

  const showWidgetPerc = detail => {
    if (!detail?.Weightage) return null;
    const perfHeader = performanceHeader?.[0];
    let show = false;
    if (isEmployee && perfHeader?.Confirm_Objectives === 2) {
      show = true;
    } else if (!isEmployee && perfHeader?.Define_Objectives === 2) {
      show = true;
    }

    if (show) {
      return (
        <Badge className="weightage fs-6" color="warning">
          Weightage: {detail?.Weightage}%
        </Badge>
      );
    }
    return null;
  };

  // Show Add/Edit Weightage
  const showAddEditWidget = detail => {
    const perfHeader = performanceHeader?.[0];
    if (isEmployee || perfHeader?.Confirm_Objectives === 2) return null;
    return (
      <AddWeightage
        edit={detail?.Weightage !== null}
        selectedKPI={detail}
        selectedEmployee={selectedEmployee}
        refetchPerformanceDetails={refetchPerformanceDetails}
        pcmWeigtage={pcmWeigtage}
        performanceWeigtage={performanceWeigtage}
        weightagePercentage={weightagePercentage}
      />
    );
  };

  const showSelfRating = detail => {
    if (!detail?.Rating_Self) return null;
    const perfHeader = performanceHeader?.[0];
    const isHRRoute = window.location.pathname.includes('hr');

    let show = false;
    let showManagerRating = false;
    if (isEmployee && perfHeader?.Self_Assessment >= 1 && detail?.Rating_Self) {
      show = detail?.Rating_Self !== null;
      if (perfHeader?.Calibration_Result === 2) {
        showManagerRating = true;
      }
    } else if (!isEmployee && perfHeader?.Self_Assessment === 2) {
      show = true;
      if (detail?.Rating_Mgr) {
        showManagerRating = true;
      }
    }

    if (show) {
      return (
        <Badge className="ratingBadge fs-6" color="success">
          <span>Self Rating: {detail?.Rating_Self}</span>
          {(showManagerRating || isHRRoute) && (
            <>
              <img src={ratingRightArrow} alt="right arrow icon" height={12} />
              <span>Manager Rating: {detail?.Rating_Mgr || 'N/A'}</span>
            </>
          )}
        </Badge>
      );
    }
    return null;
  };

  // Show Add/Edit Self Rating
  const showAddEditSelfRating = detail => {
    const perfHeader = performanceHeader?.[0];
    if (!isEmployee || perfHeader?.Self_Assessment !== 1) return null;
    if (!detail?.Rating_Self) {
      return (
        <SelfRating
          selectedKPI={detail}
          selectedEmployee={selectedEmployee}
          refetchPerformanceDetails={refetchPerformanceDetails}
        />
      );
    }
    return (
      <SelfRating
        edit
        selectedKPI={detail}
        selectedEmployee={selectedEmployee}
        refetchPerformanceDetails={refetchPerformanceDetails}
      />
    );
  };

  const showSelfRatingComment = detail => {
    let showButton = false;
    if (performanceHeader?.[0]?.Self_Assessment === 2 && !isEmployee) {
      showButton = true;
    }
    if (isEmployee && detail?.Rating_Self_Comments) {
      showButton = true;
    }

    if (showButton) {
      return (
        <SelfRatingComment
          selectedKPI={detail}
          performanceHeader={performanceHeader}
        />
      );
    }
    return null;
  };

  const showManagerComment = detail => {
    let showButton = false;
    if (performanceHeader?.[0]?.Calibration_Result === 2 && isEmployee) {
      showButton = true;
    }
    if (!isEmployee && detail?.Rating_Mgr_Comments) {
      showButton = true;
    }

    if (showButton) {
      return (
        <ManagerComment
          selectedKPI={detail}
          performanceHeader={performanceHeader}
        />
      );
    }
    return null;
  };

  // Show Add/Edit Manager Rating
  const showAddEditManagerRating = detail => {
    const perfHeader = performanceHeader?.[0];
    if (isEmployee || perfHeader?.Manager_Review !== 1) return null;
    if (!detail?.Rating_Mgr) {
      return (
        <ManagerRating
          selectedKPI={detail}
          selectedEmployee={selectedEmployee}
          refetchPerformanceDetails={refetchPerformanceDetails}
        />
      );
    }
    return (
      <ManagerRating
        edit
        selectedKPI={detail}
        selectedEmployee={selectedEmployee}
        refetchPerformanceDetails={refetchPerformanceDetails}
      />
    );
  };

  // Show Add/Edit Manager Rating
  const showSubmitButton = () => {
    if (isCompleted) return null;
    const perfHeader = performanceHeader?.[0];

    let showButton = false;
    if (
      perfHeader?.Define_Objectives === 1 &&
      isEmployee &&
      performanceDetails?.length > 0
    ) {
      showButton = true;
    } else if (perfHeader?.Confirm_Objectives === 1 && !isEmployee) {
      showButton = true;
    } else if (perfHeader?.Self_Assessment === 1 && isEmployee) {
      showButton = true;
    } else if (perfHeader?.Manager_Review === 1 && !isEmployee) {
      showButton = true;
    } else if (perfHeader?.Calibration_Result === 2 && isEmployee) {
      showButton = true;
    } else {
      showButton = false;
    }
    return (
      <>
        {perfHeader?.Define_Objectives > 1 &&
          perfHeader?.Manager_Review < 2 &&
          !isEmployee && (
            <Button
              className="custom-secondary-btn"
              onClick={toggleMovePrev}
              title="Move to Previous Step"
            >
              Move to Previous Step
            </Button>
          )}
        <span>
          {showButton && (
            <Button className="custom-btn" onClick={toggleSubmit}>
              {`${perfHeader?.Calibration_Result === 2 ? 'Accept & Submit' : 'Save & Submit'}`}
            </Button>
          )}
        </span>
      </>
    );
  };

  // Show Delete button
  const showDeleteButton = detail => {
    let showButton = false;
    if (performanceHeader?.[0]?.Define_Objectives === 1 && isEmployee) {
      showButton = true;
    }
    if (showButton) {
      return (
        <div onClick={e => toggleDelete(detail, e)}>
          <Icon
            path={mdiTrashCanOutline}
            size={0.8}
            color="#e31b54"
            title="Delete KPI"
          />
        </div>
      );
    }
    return null;
  };

  const showAddKPIButton = () => {
    let showButton = false;
    if (
      performanceHeader?.[0]?.Define_Objectives === 1 &&
      isEmployee &&
      performanceDetails?.filter(item => item?.KPI_Type === 'PERFORMANCE')
        ?.length < 6 &&
      selectedPeriod === getCurrentFinancialPeriod()
    ) {
      showButton = true;
    }
    if (showButton) {
      return (
        <Button
          className="custom-btn"
          size="md"
          onClick={toggleOffcanvasKPI}
          title="Add a new KPI"
        >
          <img src={addIcon} alt="Add a new KPI" height={14} />
        </Button>
      );
    }
    return null;
  };

  // Show Edit
  const showEditButton = detail => {
    let showButton = false;
    if (performanceHeader?.[0]?.Define_Objectives === 1 && isEmployee) {
      showButton = true;
    }
    if (showButton) {
      return (
        <Box onClick={e => toggleOffcanvasKPI(detail, e)}>
          <img
            src={rightArrowIcon}
            alt="Edit KPI details"
            height={12}
            style={{ marginLeft: '0.5em' }}
            title="Edit KPI details"
          />
        </Box>
      );
    }

    return (
      <img
        src={rightArrowIcon}
        alt="View KPI details"
        height={12}
        style={{ marginLeft: '0.5em' }}
        title="View KPI details"
      />
    );
  };

  const kpiVisibiity = showKPIs(isEmployee, performanceHeader);

  const handleBack = () => {
    if (location?.state?.from && location?.state?.activeTab) {
      navigate(location.state.from, {
        state: { activeTab: location.state.activeTab },
      });
      return;
    }

    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/hr/employee-performance');
    }
  };

  return (
    <div
      className="page-content px-0"
      style={{
        paddingTop: '80px',
        backgroundColor: 'white',
        overflowX: 'auto',
      }}
    >
      {isKPILoading ? (
        <div className="loaderContainer">
          <Spinner size="lg" color="primary" />
        </div>
      ) : (
        <>
          <div className="titleHeader">
            {employeeId && window.location.pathname.includes('/hr') ? (
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-link p-0 back-btn fs-5"
                  onClick={() => handleBack()}
                >
                  <Icon path={mdiChevronLeft} size={1.1} />
                  <span className="back fs-5">Back</span>
                </button>
                <div
                  style={{
                    width: '1px',
                    borderLeft: '1px solid lightgrey',
                    height: '20px',
                  }}
                />
                <div className="fw-semibold fs-4">Performance</div>
              </div>
            ) : (
              <div className="fw-semibold fs-4">Performance</div>
            )}
            <div className="buttonRow">
              <Button
                className="custom-light-btn btn btn-secondary"
                onClick={handleExportExcel}
                title="Download performance data as Excel"
              >
                <img src={downloadIcon} alt="" height={14} />
                Download
              </Button>
              {/* <Button
            className="btn-primary-light btn btn-primary"
            onClick={handleSave}
          >
            Save
          </Button> */}
              {showSubmitButton()}
            </div>
          </div>
          <div
            className="d-flex flex-row align-items-center justify-content-between"
            style={{
              borderBottom: '1px solid #c4c4c4',
              padding: '0.65rem 1.25rem',
            }}
          >
            <div className="d-flex flex-row align-items-center gap-3">
              <div className="fw-semibold text-nowrap fs-6">Employee</div>
              <ManagerAutocompleteSelect
                mappedFn={rawData =>
                  rawData.map(item => ({
                    value: item.value,
                    label: item.label,
                    name: item.name,
                    code: item.code,
                    designation: item.designation,
                    image: item?.Imagefile,
                  }))
                }
                value={selectedEmployee}
                onChange={option => setSelectedEmployee(option)}
                isClearable={false}
                displayOption="bothCustom"
                inputWidth="525px"
                width="100%"
                inputIcon={
                  <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                }
                rightKey="value"
                defaultShowMenu
              />
            </div>
            <div className="d-flex flex-row align-items-center gap-3">
              <div className="fw-semibold text-nowrap fs-6">Fiscal Year</div>
              <Input
                id="exampleSelect"
                className="fw-semibold external-filter-input fs-6"
                name="select"
                type="select"
                onChange={e => {
                  setSelectedPeriod(e.target.value);
                }}
              >
                {currentFiscalYear?.map(year => (
                  <option key={year?.Description} value={year?.Description}>
                    {year?.Description}
                  </option>
                ))}
              </Input>
            </div>
          </div>
          <HeaderSteps
            performanceHeader={performanceHeader}
            isLoading={performanceHeaderLoading}
          />

          <div className="contentBody">
            <div className="summary">
              <EmployeeDetails
                performanceHeader={performanceHeader}
                isLoading={performanceHeaderLoading}
                selectedPeriod={selectedPeriod}
                selectedEmployee={selectedEmployee}
                performanceDueDate={performanceDueDate}
              />
              {isCalibrationCompleted && (
                <Summary
                  performanceHeader={performanceHeader}
                  performanceDetails={performanceDetails}
                />
              )}
            </div>
            <div className="performanceDetails">
              <div className="performanceHeader">
                <div className="performanceTitle">
                  <img src={performanceHeaderIcon} alt="" height={42} />
                  <div className="titleName fw-semibold fs-5">
                    Performance -{' '}
                    <span className="titlePercent">
                      {weightagePercentageLoading
                        ? 'Loading...'
                        : `${getPerformanceWeightage(weightagePercentage)?.performanceWeightage || 0}%`}
                    </span>
                  </div>
                </div>
                {showAddKPIButton()}
              </div>
              <hr style={{ border: '1px solid #98a2b3', margin: '12px 0' }} />
              <div className="performanceContent">
                <PeroformanceKPIHeader />
                {kpiVisibiity && (
                  <div className="accordionContent">
                    <Accordion open={open} toggle={toggle}>
                      {performanceDetails
                        ?.filter(item => item?.KPI_Type === 'PERFORMANCE')
                        ?.map((detail, index) => (
                          <AccordionItem
                            className={`accordion-item ${open === `${index}` ? 'active' : ''}`}
                            style={{
                              border: '2px solid #D0D5DD',
                              borderRadius: '8px',
                              padding: '1px',
                            }}
                          >
                            <AccordionHeader
                              targetId={`${index}`}
                              key={index}
                              onClick={e => toggle(e, `${index}`)}
                            >
                              <div className="accordHeader">
                                <div className="title fs-6">
                                  {detail?.KPI_Name}
                                </div>
                                <div
                                  className="action"
                                  style={{
                                    display: 'flex',
                                    gap: '5px',
                                    marginLeft: 'auto',
                                  }}
                                >
                                  {showSelfRating(detail)}
                                  {showWidgetPerc(detail)}
                                  {showComment(detail)}
                                  {open !== `${index}` &&
                                    showAddEditWidget(detail)}
                                  {open !== `${index}` &&
                                    showAddEditSelfRating(detail)}
                                  {open !== `${index}` &&
                                    showAddEditManagerRating(detail)}
                                  {showDeleteButton(detail)}
                                  {showEditButton(detail)}
                                </div>
                              </div>
                            </AccordionHeader>
                            <AccordionBody
                              accordionId={`${index}`}
                              style={{ color: '#344054' }}
                            >
                              <div className="per-accordBody">
                                <div className="description fs-6">
                                  {parse(detail?.KPI_Details || 'N/A')}
                                </div>
                                {showSelfRatingComment(detail)}
                                {showManagerComment(detail)}
                              </div>
                              {open === `${index}` && (
                                <div className="d-flex flex-row flex-wrap justify-content-start align-items-center pt-2 gap-2">
                                  {showAddEditWidget(detail)}
                                  {showAddEditSelfRating(detail)}
                                  {showAddEditManagerRating(detail)}
                                </div>
                              )}
                            </AccordionBody>
                          </AccordionItem>
                        ))}
                    </Accordion>
                  </div>
                )}
              </div>
            </div>
            <div className="pcmDetails">
              <div className="pcmHeader">
                <div className="pcmTitle">
                  <img src={pcmHeader} alt="" height={42} />
                  <div className="titleName fw-semibold fs-5">
                    PCM -{' '}
                    <span className="titlePercent">
                      {weightagePercentageLoading
                        ? 'Loading...'
                        : `${getPerformanceWeightage(weightagePercentage)?.pcmWeightage || 0}%`}
                    </span>
                  </div>
                </div>
              </div>
              <hr style={{ border: '1px solid #98a2b3', margin: '12px 0' }} />
              <div className="pcmContent">
                <PCMKPIHeader />
                {kpiVisibiity && (
                  <div className="accordionContent">
                    <Accordion open={open} toggle={toggle}>
                      {performanceDetails
                        ?.filter(item => item?.KPI_Type === 'PCM')
                        ?.map((detail, index) => (
                          <AccordionItem
                            className={`accordion-item ${open === `pcm${index}` ? 'active' : ''}`}
                          >
                            <AccordionHeader
                              targetId={`pcm${index}`}
                              key={index}
                              onClick={e => toggle(e, `pcm${index}`)}
                            >
                              <div className="accordHeader">
                                <div className="title fs-6">
                                  {detail?.KPI_Name}
                                </div>
                                <div
                                  className="action"
                                  style={{
                                    display: 'flex',
                                    gap: '5px',
                                    marginLeft: 'auto',
                                  }}
                                >
                                  {showSelfRating(detail)}
                                  {showWidgetPerc(detail)}
                                  {showComment(detail)}
                                  {open !== `pcm${index}` &&
                                    showAddEditWidget(detail)}
                                  {open !== `pcm${index}` &&
                                    showAddEditSelfRating(detail)}
                                  {open !== `pcm${index}` &&
                                    showAddEditManagerRating(detail)}
                                  <img
                                    src={rightArrowIcon}
                                    alt="right arrow icon"
                                    height={12}
                                    style={{ marginLeft: '0.5em' }}
                                  />
                                </div>
                              </div>
                            </AccordionHeader>
                            <AccordionBody accordionId={`pcm${index}`}>
                              <div className="per-accordBody">
                                <div className="description fs-6 pt-2">
                                  {parse(detail?.KPI_Details || 'N/A')}
                                </div>
                                {showSelfRatingComment(detail)}
                                {showManagerComment(detail)}
                              </div>
                              {open === `pcm${index}` && (
                                <div className="d-flex flex-row flex-wrap justify-content-start align-items-center pt-2 gap-2">
                                  {showAddEditWidget(detail)}
                                  {showAddEditSelfRating(detail)}
                                  {showAddEditManagerRating(detail)}
                                </div>
                              )}
                            </AccordionBody>
                          </AccordionItem>
                        ))}
                    </Accordion>
                  </div>
                )}
              </div>
            </div>
          </div>
          <AddKPI
            isOpen={offcanvasKPI}
            toggle={toggleOffcanvasKPI}
            selectedKPI={selectedKPI}
            KPIs={KPIs}
            selectedEmployee={selectedEmployee}
            performanceDetails={performanceDetails}
            refetchPerformanceDetails={refetchPerformanceDetails}
            performanceHeader={performanceHeader}
            refetchPerformanceHeader={refetchPerformanceHeader}
            isEmployee={isEmployee}
          />
          <KPIComment
            isOpen={offcanvasComment}
            toggle={toggleOffcanvasComment}
            kpi={selectedKPI}
            selectedEmployee={selectedEmployee}
            refetchPerformanceDetails={refetchPerformanceDetails}
            isEmployee={isEmployee}
            isCalibrationCompleted={isCalibrationCompleted}
          />
          <ConfirmationModal
            hasPreviousCall={hasPreviousCall}
            selectedEmployee={selectedEmployee}
            performanceHeader={performanceHeader}
            refetchPerformanceHeader={refetchPerformanceHeader}
            heading={modalConfirmTitle}
            isModal={modalConfirm}
            toggleModal={() => setModalConfirm(false)}
          />
          <AcceptSubmitModal
            isOpen={modalSubmit}
            toggle={() => setModalSubmit(false)}
            selectedEmployee={selectedEmployee}
            refetchPerformanceHeader={refetchPerformanceHeader}
            refetchPerformanceDetails={refetchPerformanceDetails}
          />
          <DeleteModal
            selectedKPI={selectedKPI}
            selectedEmployee={selectedEmployee}
            refetchPerformanceDetails={refetchPerformanceDetails}
            isOpen={modalDelete}
            toggle={() => setModalDelete(false)}
          />
        </>
      )}
    </div>
  );
}

export default Performance;
